<script setup>
// A member combining their own two accounts (ACCOUNT_IDENTITY_PLAN.md). They are
// signed in as one; they prove the other with its email + password, or with a pending
// identity token from a fresh Apple/Google authorisation (ExternalLogin intent "prove").
// Nothing happens until they have read which account is kept and confirmed.
import {endpoints} from "~/network/endpoints.js";
import SocialLoginButtons from '~/components/auth/SocialLoginButtons.client.vue';
import {COMBINE_CONFIRMATION, combineConfirmationAccepted} from '~/composables/useAccountCombine.js';

const props = defineProps({
  modelValue: {type: Boolean, default: false},
  // Pre-proven social identity (from the profile page): skip the password step.
  pendingIdentityToken: {type: String, default: null},
  pendingProvider: {type: String, default: null},
});
const emit = defineEmits(['update:modelValue', 'combined', 'done']);

const {httpRequest} = useHttpRequest();
const {adoptSession, user} = useAuthService();

const step = ref('prove'); // prove | confirm | done
const email = ref('');
const password = ref('');
const showPassword = ref(false);
const pending = ref(false);
const errors = ref(null);
const preview = ref(null);
const confirmText = ref('');

const open = computed({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});
watch(open, (v) => {
  if (!v) return;
  step.value = 'prove';
  email.value = '';
  password.value = '';
  errors.value = null;
  preview.value = null;
  proven.value = null;
  confirmText.value = '';
  // A proven social identity goes straight to the preview.
  if (props.pendingIdentityToken) runPreview();
});

// Proven in the dialog itself with Google/Apple (the other account may have no password).
const proven = ref(null); // {token, provider}
const proof = () => props.pendingIdentityToken
    ? {pendingIdentityToken: props.pendingIdentityToken}
    : proven.value
        ? {pendingIdentityToken: proven.value.token}
        : {email: email.value.trim(), password: password.value};
const onProven = (p) => {
  proven.value = p;
  runPreview();
};

const runPreview = async () => {
  pending.value = true;
  errors.value = null;
  try {
    preview.value = await httpRequest(endpoints.auth.combineAccountsPreview, proof());
    step.value = 'confirm';
  } catch (e) {
    console.error('combine preview failed', e);
    errors.value = e?.global?.length ? e : {global: [e?.global?.[0] || 'Could not check that account']};
  } finally {
    pending.value = false;
  }
};

const canCombine = computed(() => step.value === 'confirm' && combineConfirmationAccepted(confirmText.value));
const combine = async () => {
  pending.value = true;
  errors.value = null;
  try {
    const res = await httpRequest(endpoints.auth.combineAccounts, proof());
    // The kept account is the session from here on.
    adoptSession(res.user);
    step.value = 'done';
    emit('combined', res);
  } catch (e) {
    console.error('combine failed', e);
    errors.value = e?.global?.length ? e : {global: [e?.global?.[0] || 'The accounts were not combined']};
  } finally {
    pending.value = false;
  }
};

const methods = (u) => [u.hasPassword ? 'email + password' : null, ...(u.providers || []).map(p => p === 'apple' ? 'Apple' : p === 'google' ? 'Google' : p)].filter(Boolean).join(', ') || 'no sign-in method';
const summary = (u) => `${u.clubCount} club${u.clubCount === 1 ? '' : 's'} · ${u.resultCount} result${u.resultCount === 1 ? '' : 's'} · ${methods(u)}`;
</script>

<template>
  <tl-dialog v-model="open" title="Combine your accounts" :max-width="520">
    <!-- Step 1: prove the other account -->
    <template v-if="step === 'prove'">
      <p class="text-body-2 mb-4">
        You're signed in as <b>{{ user.email }}</b>. Prove your <b>other</b> Tap Leagues account with its email and
        password, or the Google/Apple sign-in it uses. We'll show you exactly what will happen before anything changes.
      </p>
      <div class="tl-label mb-1">Other account's email</div>
      <v-text-field v-model="email" type="email" density="compact" placeholder="you@club.com" hide-details="auto"
                    prepend-inner-icon="mdi-email-outline" data-testid="combine-email"/>
      <div class="tl-label mb-1 mt-3">Its password</div>
      <v-text-field v-model="password" :type="showPassword ? 'text' : 'password'" density="compact" placeholder="••••••••"
                    hide-details="auto" prepend-inner-icon="mdi-lock-outline" data-testid="combine-password"
                    :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                    @click:append-inner="showPassword = !showPassword" @keyup.enter="runPreview"/>
      <crud-error-display class="mt-3" :errors="errors" v-if="errors"/>
      <div class="cmb-or"><span>or prove it with</span></div>
      <social-login-buttons type="prove" @identity-proven="onProven"/>
    </template>

    <!-- Step 2: the warning, in full -->
    <template v-else-if="step === 'confirm' && preview">
      <v-alert type="warning" variant="tonal" density="comfortable" class="mb-4" data-testid="combine-warning">
        <div class="text-body-2">
          <strong>This can't be undone.</strong> Two accounts become one. Everything from the account being folded
          in (clubs, matches, results, messages, payments) moves to the kept account. Its email will no longer
          sign in on its own; its Apple or Google sign-in will open the kept account instead.
        </div>
      </v-alert>

      <div class="cmb-pair">
        <div class="cmb-side cmb-side--keep">
          <div class="cmb-tag">Kept</div>
          <div class="cmb-name">{{ preview.keep.firstName }} {{ preview.keep.lastName }}</div>
          <div class="cmb-email">{{ preview.keep.email }}<span v-if="preview.keepIsCurrent" class="tl-chip ml-2">you're signed in as this one</span></div>
          <div class="cmb-meta">{{ summary(preview.keep) }}</div>
        </div>
        <div class="cmb-side cmb-side--drop">
          <div class="cmb-tag">Folded in and closed</div>
          <div class="cmb-name">{{ preview.drop.firstName }} {{ preview.drop.lastName }}</div>
          <div class="cmb-email">{{ preview.drop.email }}<span v-if="!preview.keepIsCurrent" class="tl-chip ml-2">you're signed in as this one</span></div>
          <div class="cmb-meta">{{ summary(preview.drop) }}</div>
        </div>
      </div>
      <p class="text-caption text-medium-emphasis mt-3 mb-0">
        The account with your clubs and results is the one kept. If the kept account isn't the one you're signed in
        as, you'll be switched to it when this finishes.
      </p>
      <div v-if="preview.duplicateEntriesRemoved?.length" class="text-caption mt-2">
        You had an entry in the same competition on both accounts; the kept account's entry stays:
        {{ preview.duplicateEntriesRemoved.join(', ') }}.
      </div>

      <div class="tl-label mt-4 mb-1">Type {{ COMBINE_CONFIRMATION }} to confirm</div>
      <v-text-field v-model="confirmText" density="compact" hide-details :placeholder="COMBINE_CONFIRMATION" data-testid="combine-confirm-text"/>
      <crud-error-display class="mt-3" :errors="errors" v-if="errors"/>
    </template>

    <!-- Step 3: done -->
    <template v-else-if="step === 'done'">
      <div class="d-flex align-center ga-3">
        <v-icon size="26" color="success">mdi-check-circle</v-icon>
        <div class="text-body-2">
          Done. You're now signed in as <b>{{ user.email }}</b> and everything is in one place. We've emailed both
          addresses to confirm.
        </div>
      </div>
    </template>

    <template #actions>
      <v-spacer/>
      <template v-if="step === 'prove'">
        <v-btn variant="outlined" :disabled="pending" @click="open = false">Cancel</v-btn>
        <v-btn color="primary" variant="flat" :loading="pending" :disabled="!email || !password" data-testid="combine-check" @click="runPreview">
          Check the other account
        </v-btn>
      </template>
      <template v-else-if="step === 'confirm'">
        <v-btn variant="outlined" :disabled="pending" @click="open = false">Keep them separate</v-btn>
        <v-btn color="error" variant="flat" :disabled="!canCombine" :loading="pending" data-testid="combine-go" @click="combine">
          Combine accounts
        </v-btn>
      </template>
      <template v-else>
        <v-btn color="primary" variant="flat" data-testid="combine-continue" @click="open = false; emit('done')">Continue</v-btn>
      </template>
    </template>
  </tl-dialog>
</template>

<style scoped lang="scss">
.cmb-or { display: flex; align-items: center; gap: 10px; margin: 14px 0 10px; font-size: 12px; color: rgb(var(--v-theme-textSubtle)); }
.cmb-or::before, .cmb-or::after { content: ""; flex: 1; height: 1px; background: rgb(var(--v-theme-borderLight)); }
.cmb-pair { display: flex; flex-direction: column; gap: 10px; }
.cmb-side { padding: 12px 14px; border-radius: 12px; border: 1px solid rgb(var(--v-theme-borderLight)); }
.cmb-side--keep { border-color: rgb(var(--v-theme-success)); background: rgba(var(--v-theme-success), .05); }
.cmb-side--drop { border-color: rgb(var(--v-theme-error)); background: rgba(var(--v-theme-error), .04); }
.cmb-tag { font-size: 10.5px; font-weight: 800; letter-spacing: .5px; text-transform: uppercase; margin-bottom: 4px; }
.cmb-side--keep .cmb-tag { color: rgb(var(--v-theme-success)); }
.cmb-side--drop .cmb-tag { color: rgb(var(--v-theme-error)); }
.cmb-name { font-size: 14px; font-weight: 700; }
.cmb-email { font-size: 12.5px; word-break: break-all; }
.cmb-meta { font-size: 11.5px; color: rgb(var(--v-theme-textSubtle)); margin-top: 2px; }
</style>
